// src/components/shared/monitoringStyles.js
// Shared theming helpers for the Monitoring tab's summary tables
// (Real-Time / File / Batch — unifiedEventSummary.js, unifiedFileSummary.js,
// unifiedBatchSummary.js). These pages each define their own
// muiTableHeadCellProps/muiTableBodyCellProps (so they aren't affected by
// sharedTableProps' cell styling) and previously used hardcoded gray/green
// colors instead of the app theme. This file gives them a common,
// theme-driven look: each metric column-group (e.g. "EDS Events",
// "Integration Errors", "EDS Kickouts") gets its own accent color cycling
// through the theme palette, and the toolbar's summary stats/legend use
// shared Chip-based components instead of raw styled <span> text.
import React from "react";
import { alpha } from "@mui/material/styles";
import { Box, Chip } from "@mui/material";

// Cycles if a page has more groups than colors defined here.
const GROUP_ACCENT_KEYS = ["primary", "warning", "secondary"];

/** Resolve the accent color for the Nth metric group on a page (0-indexed,
 * in the order the groups appear left-to-right). */
export function getGroupAccent(theme, index) {
  const key = GROUP_ACCENT_KEYS[index % GROUP_ACCENT_KEYS.length];
  const color = theme.palette[key].main;
  return {
    key,
    color,
    headerBg: alpha(color, 0.1),
    border: `3px solid ${color}`,
  };
}

/** A single labeled stat in the table's top toolbar, e.g.
 * "Last Refreshed: Aug 21, 2026 3:51 PM". `color` accepts any theme color
 * path ("success.main", "error.main", "text.primary", ...) or a literal. */
export function ToolbarStat({ label, value, color = "text.primary" }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mr: 3.5 }}>
      <Box component="span" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
        {label}:
      </Box>
      <Box component="span" sx={{ fontSize: "0.875rem", fontWeight: 700, color }}>
        {value}
      </Box>
    </Box>
  );
}

const LEGEND_ITEMS = [
  ["T", "Today"],
  ["Y", "Yesterday"],
  ["7D", "Last 7 Days"],
  ["30D", "Last 30 Days"],
];

/** The T / Y / 7D / 30D legend, shown as small outlined chips pinned to the
 * right edge of the toolbar. */
export function ToolbarLegend() {
  return (
    <Box sx={{ display: "flex", gap: 0.75, ml: "auto", flexWrap: "wrap" }}>
      {LEGEND_ITEMS.map(([abbr, full]) => (
        <Chip
          key={abbr}
          size="small"
          variant="outlined"
          label={`${abbr} = ${full}`}
          sx={{
            height: 22,
            fontSize: "0.7rem",
            borderColor: "divider",
            color: "text.secondary",
            "& .MuiChip-label": { px: 1 },
          }}
        />
      ))}
    </Box>
  );
}

/** Common wrapper for the toolbar row across the Monitoring pages. The stat
 * chips (Last Refreshed, totals, etc.) and an optional `actions` node (e.g.
 * a button that opens a popup, placed to their left) are centered together
 * as one group in the middle of the toolbar — matching the original layout
 * — while the T/Y/7D/30D legend stays pinned to the right edge. */
export function ToolbarRow({ children, actions }) {
  const items = React.Children.toArray(children);
  const legend = items.filter((child) => child.type === ToolbarLegend);
  const stats = items.filter((child) => child.type !== ToolbarLegend);

  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%", py: 0.5 }}>
      <Box sx={{ flex: 1 }} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
          rowGap: 1,
        }}
      >
        {actions}
        {stats}
      </Box>
      <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>{legend}</Box>
    </Box>
  );
}
